/**
 * President's Report Homepage Background Videos Functionality
 *
 * This JavaScript file provides functionality for handling background videos on the report homepage.
 *
 * It detects mobile devices, user preferences for reduced motion, and manages the display and
 * behavior of background videos accordingly.
 *
 * The script sets up custom video controls and handles the playing/pausing of videos as the user scrolls.
 *
 * Dependencies:
 * - mobileBreakpoint, elonPresReportContent: Imported from 'config.js' for consistency in responsive 
 *   design breakpoints and DOM element reference.
 * - isInViewport: This function is imported from elon-modules and is used to
 *   check if an element is in the viewport.
 */

// Import custom utilities
import { mobileBreakpoint, elonPresReportContent } from '../modules/config.js';
import isInViewport from 'elon-modules/js/environment/element-viewport-check';

document.addEventListener('DOMContentLoaded', () => {
	
	/**
	 * Homepage Background Videos Functionality Helper Functions
	 */
	 
	// Function to play the video and update control button
	const playVideo = (video, playPauseBtnIcon, playPauseBtnText, dataSectionName) => {
		// Play the video
		video.play();
	
		// Update the button icon to 'Pause'
		playPauseBtnIcon.classList.remove('fa-play');
		playPauseBtnIcon.classList.add('fa-pause');
	
		// Update the button text to 'Pause'
		playPauseBtnText.textContent = `Pause ${dataSectionName} Background Video`;
		
		// Add the fade-in class if the video does not have the class no-fade
		if (!video.classList.contains('no-fade')) {
			video.classList.add('fade-in');
		}
	};
	
	// Function to pause the video and update control button
	const pauseVideo = (video, playPauseBtnIcon, playPauseBtnText, dataSectionName) => {
		// Pause the video
		video.pause();
	
		// Update the button icon to 'Play'
		playPauseBtnIcon.classList.remove('fa-pause');
		playPauseBtnIcon.classList.add('fa-play');
	
		// Update the button text to 'Play'
		playPauseBtnText.textContent = `Play ${dataSectionName} Background Video`;
		
		// Add the fade-in class if the video does not have the class no-fade
		if (!video.classList.contains('no-fade')) {
			video.classList.remove('fade-in');
		}
	};
	
	// Helper function to get elements associated with a video's controls
	const getVideoControlElements = (video) => {
		const section = video.closest('.pr-section');
		const controlsContainer = section.querySelector('.section--background-video-controls');
		const playPauseBtn = controlsContainer.querySelector('.background-video--play-pause-btn');
		const playPauseBtnIcon = playPauseBtn.querySelector('.play-pause-btn--icon');
		const playPauseBtnText = playPauseBtn.querySelector('.play-pause-btn--text');
		const dataSectionName = playPauseBtn.getAttribute('data-section-name');
	
		return {
			controlsContainer,
			playPauseBtn,
			playPauseBtnIcon,
			playPauseBtnText,
			dataSectionName
		};
	};
	
	// Function to set up the video controls
	const setupVideoControls = (videos) => {
		videos.forEach((video) => {
			// Retrieve video control elements
			const { controlsContainer, playPauseBtn, playPauseBtnIcon, playPauseBtnText, dataSectionName } = getVideoControlElements(video);
			
			// Set up click events for control button
			playPauseBtn.addEventListener('click', () => {
				if (video.paused) {
					playVideo(video, playPauseBtnIcon, playPauseBtnText, dataSectionName);
					// Video is played by the user, so remove the "user paused" data attribute
					video.removeAttribute('data-user-paused');
				} else {
					pauseVideo(video, playPauseBtnIcon, playPauseBtnText, dataSectionName);
					// Video is paused by the user, so set a data attribute to indicate this
					video.setAttribute('data-user-paused', 'true');
				}
			});
			
			// Fade the controls in on load
			controlsContainer.classList.add('fade-in');
		});
	};
	
	// Function to handle playing/pausing of background videos
	const handleBackgroundVideos = (videos) => {
		// Iterate over each video element
		videos.forEach((video) => {
			
			// Make sure the video is not hidden
			if (video.getAttribute('data-video-hidden') !== 'true') {
				
				// Establish how much of the video we want to show before it starts playing
				const videoIsInView = isInViewport(video, 45);
				
				// Retrieve video control elements
				const { controlsContainer, playPauseBtn, playPauseBtnIcon, playPauseBtnText, dataSectionName } = getVideoControlElements(video);
				
				// Check if the video is in view and it hasn't been paused manually
				if (videoIsInView && video.getAttribute('data-user-paused') !== 'true') {
					// Start playing the video
					playVideo(video, playPauseBtnIcon, playPauseBtnText, dataSectionName);
				} else {
					// Pause video if not in viewport
					pauseVideo(video, playPauseBtnIcon, playPauseBtnText, dataSectionName);
				}
				
			}
			
		});
	};
	
	/**
	 * Homepage Background Videos Functionality
	 */
	
	// Check if this is the report homepage template
	if (elonPresReportContent !== null) {
		
		// Check for various attributes and settings
		const body = document.body;
		const isMobileDevice = document.body.getAttribute('data-mobile-device') !== null;
		const userPrefersReducedMotion = document.body.getAttribute('data-reduced-motion') !== null;
		
		if (userPrefersReducedMotion) {
			
			// Hide all videos and controls
			const backgroundVideoContainers = document.querySelectorAll('.background--video-container');
			
			if (backgroundVideoContainers.length > 0) {
				backgroundVideoContainers.forEach(container => {
					container.style.display = 'none';
				});
			}
			
		} else {
			
			// Display all videos and controls if "desktop" view and not mobile browser
			if (window.innerWidth > mobileBreakpoint && !isMobileDevice) {
				
				const backgroundVideoContainers = document.querySelectorAll('.background--video-container');
				
				if (backgroundVideoContainers.length > 0) {
					backgroundVideoContainers.forEach(container => {
						container.style.display = 'block';
						container.querySelector('video').setAttribute('data-video-hidden', 'false');
					});
				}
				
			}
			
			// Get all background video elements that are not hidden
			const backgroundVideos = document.querySelectorAll('.background-video:not([data-video-hidden="true"])');
			
			// Proceed if any visible background videos are found
			if (backgroundVideos.length > 0) {
				
				// Set up our custom video controls
				setupVideoControls(backgroundVideos);
				
				// Add event listener for scroll event
				window.addEventListener('scroll', () => {
					// Call handleBackgroundVideos function to handle background videos on scroll
					handleBackgroundVideos(backgroundVideos);
				});
				
				// Initial handling of videos
				handleBackgroundVideos(backgroundVideos);
				
			}
			
		}
		
	}
	
});